
import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Package, 
  ListTree, 
  ShoppingCart as OrdersIcon, 
  Settings, 
  LogOut,
  ArrowLeft,
  MessageSquare,
  DollarSign,
  BarChart3,
  ShieldCheck,
  Store as StoreIcon,
  CreditCard,
  FileText,
  AlertTriangle,
  ArrowRight,
  Truck,
  Users,
  Zap,
  Ticket,
  Boxes,
  Share2,
  Award,
  Megaphone,
  LayoutGrid,
  Video,
  BrainCircuit
} from 'lucide-react';
import { LOGO } from '../constants';
import { User, Store, UserRole, StaffRole } from '../types';
import { storage } from '../lib/storage';
import { Language, translations } from '../lib/i18n';
import { staffService } from '../services/staffService';

interface AdminLayoutProps {
  children: React.ReactNode;
  onNavigate: (page: any) => void;
  activeTab: string;
  user: User | null;
  lang: Language;
  onLangChange: (lang: Language) => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, onNavigate, activeTab, user, lang, onLangChange }) => {
  const [store, setStore] = useState<Store | null>(null);
  const t = translations[lang];

  useEffect(() => {
    if (user?.storeId) {
      setStore(storage.getStoreConfig(user.storeId));
    }
  }, [user]);

  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isOverdue = store?.subscription?.status === 'OVERDUE';

  const sellerMenuItems = [
    { id: 'admin', label: t.dashboard, icon: LayoutDashboard },
    { id: 'analytics', label: t.analytics, icon: BarChart3 },
    { id: 'flash-sales', label: 'Flash Sales', icon: Zap },
    { id: 'ai-pricing', label: 'AI Pricing Engine', icon: BrainCircuit },
    { id: 'bundles', label: 'Bundle Architect', icon: LayoutGrid },
    { id: 'social-commerce', label: 'Social Video Hub', icon: Video },
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'sub-boxes', label: 'Sub Boxes', icon: Boxes },
    { id: 'vip-club', label: 'VIP Club', icon: Award },
    { id: 'lp-architect', label: 'AI Landing Pages', icon: Megaphone },
    { id: 'influencers', label: 'Influencer Hub', icon: Share2 },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'finances', label: 'Finances', icon: DollarSign },
    { id: 'shipping', label: 'Shipping Rules', icon: Truck },
    { id: 'taxation', label: 'Taxation', icon: FileText },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'team', label: 'My Team', icon: Users },
    { id: 'products', label: t.products, icon: Package },
    { id: 'categories', label: t.categories, icon: ListTree },
    { id: 'orders', label: t.orders, icon: OrdersIcon },
    { id: 'settings', label: t.settings, icon: Settings },
  ];

  const superAdminMenuItems = [
    { id: 'super-admin', label: 'Command Center', icon: ShieldCheck },
    { id: 'analytics', label: 'Platform Intel', icon: BarChart3 },
    { id: 'finances', label: 'Settlements', icon: DollarSign },
    { id: 'settings', label: 'Global Rules', icon: Settings },
  ];

  // Filter menu items by role permission
  const filteredSellerItems = sellerMenuItems.filter(item => 
    staffService.hasPermission(user?.staffRole, item.id)
  );

  const menuItems = isSuperAdmin ? superAdminMenuItems : filteredSellerItems;

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col hidden md:flex">
        <div className="p-6">
          {LOGO}
          <div className={`mt-6 p-4 rounded-2xl border ${isSuperAdmin ? 'bg-gray-900 border-gray-800' : 'bg-blue-50 border-blue-100'}`}>
             <div className="flex items-center gap-3">
               <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-black ${isSuperAdmin ? 'bg-blue-600' : 'bg-blue-600'}`}>
                 {isSuperAdmin ? 'SA' : (store?.name?.[0].toUpperCase() || 'S')}
               </div>
               <div className="min-w-0">
                 <p className={`text-[10px] font-black uppercase tracking-widest ${isSuperAdmin ? 'text-blue-400' : 'text-blue-600'}`}>
                   {isSuperAdmin ? 'Global Platform' : user?.staffRole ? user.staffRole : 'Owner'}
                 </p>
                 <p className={`text-xs font-bold truncate ${isSuperAdmin ? 'text-white' : 'text-gray-900'}`}>
                   {isSuperAdmin ? 'Lynqar HQ' : (store?.name || 'Loading...')}
                 </p>
               </div>
             </div>
          </div>
        </div>
        
        <nav className="flex-grow px-4 space-y-1 mt-4 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id as any)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                activeTab === item.id 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' 
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon className={`h-5 w-5 ${activeTab === item.id ? 'text-white' : 'text-gray-400'}`} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button 
            onClick={() => onNavigate('home')}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
          >
            <LogOut className="h-5 w-5" />
            Exit Dashboard
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-grow flex flex-col min-w-0">
        {/* Overdue Warning Bar */}
        {!isSuperAdmin && isOverdue && (
          <div className="bg-red-600 text-white py-2 px-8 flex items-center justify-between animate-in slide-in-from-top duration-500">
             <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                <AlertTriangle className="h-3 w-3" /> Subscription Overdue - Access Limited
             </div>
             <button onClick={() => onNavigate('billing')} className="text-[10px] font-black uppercase underline hover:no-underline flex items-center gap-1">
                Pay Invoice <ArrowRight className="h-3 w-3" />
             </button>
          </div>
        )}

        <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => onNavigate('home')}
              className="md:hidden text-gray-600"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-black capitalize text-gray-900 tracking-tight">{activeTab.replace('-', ' ')}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-50 rounded-2xl p-1 border border-gray-100 mr-2">
              <button 
                onClick={() => onLangChange('en')}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all ${lang === 'en' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                EN
              </button>
              <button 
                onClick={() => onLangChange('hi')}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all ${lang === 'hi' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                हिं
              </button>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-black text-gray-900">{user?.email}</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{user?.staffRole ? `Staff: ${user.staffRole}` : user?.role}</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center font-black border border-gray-200 overflow-hidden">
               {isSuperAdmin ? <ShieldCheck className="h-6 w-6 text-blue-600" /> : user?.email?.[0].toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-grow p-8 overflow-y-auto bg-gray-50/50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
